import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Section }        from '../../02-Molecules/Section/Section'
import { ButtonApplePay } from "../../02-Molecules/ButtonApplePay/ButtonApplePay"
import { ButtonPaypal }   from "../../02-Molecules/ButtonPaypal/ButtonPaypal"
import { ButtonGooglePay }  from "../../02-Molecules/ButtonGooglePay/ButtonGooglePay"

import './SectionQuickPay.scss'

import {applePayIsSupported} from "../../Utility/Utility";

/**
 * SectionQuickPay component.
 */
export const SectionQuickPay = forwardRef(({ brainTreeInstancesGet, brainTreeIsInstantiated, buttonApplePayEnabled, buttonGooglePayEnabled, buttonPayWithCardEnabled, collapsed, googleMerchantEnvironment, heading, headingLarge, loqateKey, onDoNext, onSetProcessing, paymentHasCompleted, sectionId, staticText, tracking, trackEvent, validationHash, values, donationFeeRate, ...props }, ref) => {

  /**
   * Handle the order update action.
   *
   * @param {{}} orderData
   *   The order data to update.
   */
  const handlerOrderUpdate = orderData => {
    Object.keys(orderData).forEach(key => {
      props.onInputChange(key, orderData[key])
    })

    onSetProcessing(false)
  }

  /**
   * Render the payment state.
   *
   * @returns {JSX.Element}
   */
  const statePayment = () => {
    return (
      <ul className={'o-section-quick-pay__list'}>
        {applePayIsSupported() && buttonApplePayEnabled &&
        <li>
          <ButtonApplePay
            orderUpdate={handlerOrderUpdate}
            paymentHasCompleted={paymentHasCompleted}
            onSetError={() => {}}
            onSetProcessing={onSetProcessing}
            brainTreeIsInstantiated={brainTreeIsInstantiated}
            brainTreeInstancesGet={brainTreeInstancesGet}
            values={values}
            onPaymentSelect={props.onPaymentSelect}
            donationFeeRate={donationFeeRate}
          />
        </li>
        }

        {buttonGooglePayEnabled &&
        <li>
          <ButtonGooglePay
            orderUpdate={handlerOrderUpdate}
            paymentHasCompleted={paymentHasCompleted}
            onSetError={() => {}}
            onSetProcessing={onSetProcessing}
            brainTreeIsInstantiated={brainTreeIsInstantiated}
            brainTreeInstancesGet={brainTreeInstancesGet}
            environment={googleMerchantEnvironment}
            values={values}
            onPaymentSelect={props.onPaymentSelect}
            donationFeeRate={donationFeeRate}
          />
        </li>
        }
        <li>
          <ButtonPaypal
            orderUpdate={handlerOrderUpdate}
            paymentHasCompleted={paymentHasCompleted}
            onSetError={() => {}}
            onSetProcessing={onSetProcessing}
            brainTreeIsInstantiated={brainTreeIsInstantiated}
            brainTreeInstancesGet={brainTreeInstancesGet}
            values={values}
            onPaymentSelect={props.onPaymentSelect}
            donationFeeRate={donationFeeRate}
          />
        </li>


        {buttonPayWithCardEnabled &&
        <li className={'o-section-quick-pay__list-card-option'}>
          <button className={'b-button-green-light-large'} onClick={() => {
            props.onInputChange('pay_with_card', true)
            props.onPaymentSelect(true)
          }}>Pay with card
          </button>
        </li>
        }
      </ul>
    )
  }

  /**
   * Render the payment complete state.
   *
   * @returns {JSX.Element}
   */
  const statePaymentComplete = () => {
    return <div>{staticText.quick_pay.payment_complete}</div>
  }

  return (
    <React.Fragment>
      {brainTreeInstancesGet && values.payment_amount ?
        <Section tracking={tracking} trackEvent={trackEvent} collapsed={collapsed} heading={heading} headingLarge={headingLarge} sectionId={sectionId} textBefore={staticText.quick_pay.text_before} textAfter={staticText.quick_pay.text_after}>
          {paymentHasCompleted ? statePaymentComplete() : statePayment()}
        </Section> : null
      }
    </React.Fragment>
  )
})

/**
 * SectionQuickPay PropTypes.
 */
SectionQuickPay.propTypes = {
  brainTreeIsInstantiated : PropTypes.bool,
  brainTreeInstancesGet   : PropTypes.func,
  buttonApplePayEnabled   : PropTypes.bool,
  buttonGooglePayEnabled  : PropTypes.bool,
  googleMerchantEnvironment: PropTypes.string,
  loqateKey               : PropTypes.string,
  collapsed               : PropTypes.bool,
  paymentHasCompleted     : PropTypes.bool,
  paymentError            : PropTypes.bool,
  heading                 : PropTypes.string,
  headingLarge            : PropTypes.bool,
  sectionId               : PropTypes.string,
  validationHash          : PropTypes.string,
  onInputChange           : PropTypes.func,
  onSetProcessing         : PropTypes.func,
  onDoNext                : PropTypes.func,
  staticText              : PropTypes.shape({
    quick_pay: PropTypes.shape({
      text_before     : PropTypes.string,
      payment_complete: PropTypes.string,
      text_after      : PropTypes.string,
    })
  }),
  tracking: PropTypes.shape({
    id  : PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func,
  values: PropTypes.shape({
    payment_amount           : PropTypes.string,
    first_name               : PropTypes.string,
    last_name                : PropTypes.string,
    email_address            : PropTypes.string,
    telephone_number         : PropTypes.string,
    use_contact_address      : PropTypes.bool,
    contact_address_line_1   : PropTypes.string,
    contact_address_line_2   : PropTypes.string,
    contact_address_town_city: PropTypes.string,
    contact_address_post_code: PropTypes.string,
    billing_address_line_1   : PropTypes.string,
    billing_address_line_2   : PropTypes.string,
    billing_address_town_city: PropTypes.string,
    billing_address_post_code: PropTypes.string,
    order_id                 : PropTypes.string,
    uuid                     : PropTypes.string,
  }),
  onPaymentSelect         : PropTypes.func
}

/**
 * SectionQuickPay default props.
 */
SectionQuickPay.defaultProps = {
  buttonApplePayEnabled   : true,
  buttonGooglePayEnabled  : true,
  buttonPayWithCardEnabled: false
}
